// frontend/src/components/layout/navActive.js
//
// "Is this nav destination the screen the merchant is on?" — one rule, shared
// by the sidebar and the phone's tab bar and More sheet (mobile/mobileNav.js).
//
// A nav link is active on its own path and on every descendant path, because
// the tab shell can only parameterize a tab through the PATH
// (tabs/routeSegment.js: tabs are keyed by pathname, so a query string matches
// no registered route), so a filtered or record-scoped view of a screen is
// spelled as a child path:
//
//     /item-setup/item/:itemCode        the Items grid, one item
//     /purchase-orders/:id              one PO's details

/**
 * Screens a nav link owns that do NOT sit beneath its own path, so the
 * descendant rule cannot reach them.
 *
 * One entry today. The Supplier catalog is a per-supplier record screen opened
 * from Supplier setup (and from Data Health's "Fix order rules"), but it is
 * registered under `/suppliers/:id/catalog` — a different root from the
 * `/supplier-setup` link that owns it.
 *
 * This is deliberately a short exception list and not a general route→nav map:
 * a new screen should be registered BENEATH the nav entry that owns it, and
 * needing a line here is a hint that its path is in the wrong place.
 */
const ALSO_OWNED = {
  '/supplier-setup': ['/suppliers'],
};

/** `pathname` is `path`, or lives beneath it. */
function isAtOrBeneath(path, pathname) {
  // The trailing slash is load-bearing: a bare `startsWith(path)` would let
  // `/forecast` claim `/forecast-generator`, a different (staff) screen.
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * True when `pathname` is the screen `navPath` leads to, one beneath it, or
 * one this link owns from elsewhere (`ALSO_OWNED`).
 *
 * Home is exact-only. Every path is beneath `/`, so the descendant rule would
 * light it up on every screen in the app.
 *
 * @param {string} navPath   The link's destination, e.g. '/supplier-setup'.
 * @param {string} pathname  `location.pathname`.
 */
export function isNavPathActive(navPath, pathname) {
  if (navPath === '/') return pathname === '/';
  if (isAtOrBeneath(navPath, pathname)) return true;
  return (ALSO_OWNED[navPath] || []).some((p) => isAtOrBeneath(p, pathname));
}

/**
 * True when `pathname` is at or beneath ANY of `navPaths` — the sidebar's
 * "should this collapsed section be open" question.
 *
 * @param {string[]} navPaths
 * @param {string} pathname
 */
export function isNavGroupActive(navPaths, pathname) {
  return navPaths.some((navPath) => isNavPathActive(navPath, pathname));
}
